import React, { useEffect, useState } from 'react';
import {
    Box,
    Chip,
    Stack,
    Typography,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    NoSsr,
    Popper,
    Paper
} from '@mui/material';
import { useItemTooltip, useMouseTracker } from '@mui/x-charts/ChartsTooltip';
import {
    ChartsOnAxisClickHandler,
    ResponsiveChartContainer,
    BarPlot,
    ChartsXAxis,
    ChartsClipPath,
    ChartsYAxis
} from "@mui/x-charts"
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CircleIcon from '@mui/icons-material/Circle';

import { PageTitleWithToolTip, DataGrid } from '../Common'
import { getRangeFilterOperator } from '../Common/DataGrid/FilterComponents';
import {
    text_commonCellProps, select_commonCellProps,
} from '../Common/DataGrid/utils';
import { StyledCellLink, Weight } from '../Common/DataGrid/CellComponents';

import { getChartColor, generateVirtualElement } from '../../Utils/charts_utils';
import { Grey1, Grey2 } from '../../color_utils';
import Routes from '../../routes';

const rangeFilterOperator = getRangeFilterOperator({});

const columns = [
    {
        field: 'assetName',
        headerName: 'Asset Name',
        ...text_commonCellProps,
        renderCell: ({ row: { assetName, assetId } }) => (
            <StyledCellLink
                path={`/Inventory/${assetId}`}
                text={assetName}
                from={window.location.pathname}
                primaryLink={true}
            />
        )
    },
    {
        field: 'regionDivision',
        headerName: 'Region / Division',
        ...select_commonCellProps
    },
    {
        field: 'status',
        headerName: 'Status',
        ...select_commonCellProps
    },
    {
        field: 'operationalStatus',
        headerName: 'Operational status',
        ...select_commonCellProps
    },
    {
        field: 'assetType',
        headerName: 'Type',
        ...select_commonCellProps
    },
    {
        field: 'materialClass',
        headerName: 'Material class',
        ...select_commonCellProps
    },
    {
        field: 'materialType',
        headerName: 'Material type',
        ...select_commonCellProps
    },
    {
        field: 'chargeCapacity',
        headerName: 'Charge size',
        filterOperators: rangeFilterOperator,
        renderCell: ({ row: { chargeCapacity } }) => <Weight value={chargeCapacity} />
    },
    {
        field: 'applicableRegulations',
        headerName: 'Applicable regulation',
        ...select_commonCellProps,
        width: 200,
        renderCell: ({ row: { applicableRegulations } }) => (
            <Stack flexDirection={'row'} justifyContent={"flex-end"} gap={1} sx={{ mt: '10px' }}>
                {
                    applicableRegulations && (
                        applicableRegulations.map(x => {
                            const color = getChartColor(x.name)
                            return <Chip label={x.name} variant="outlined" sx={{ borderColor: color, color: color }} />
                        })
                    )
                }
            </Stack>
        )
    },
]

const CustomItemTooltip = ({ dataSet }) => {
    const tooltipData = useItemTooltip();
    const mousePosition = useMouseTracker(); // Track the mouse position on chart.

    if (!tooltipData || !mousePosition) return null;

    const isMousePointer = mousePosition?.pointerType === 'mouse';
    const yOffset = isMousePointer ? 0 : 30 - mousePosition.height;
    const data = dataSet[tooltipData.identifier.dataIndex];

    return (
        <NoSsr>
            <Popper
                sx={{
                    pointerEvents: 'none',
                    zIndex: (theme) => theme.zIndex.modal,
                }}
                open
                placement={isMousePointer ? 'top-start' : 'top'}
                anchorEl={generateVirtualElement(mousePosition)}
                modifiers={[
                    {
                        name: 'offset',
                        options: {
                            offset: [0, yOffset],
                        },
                    },
                ]}
            >
                <Paper
                    elevation={0}
                    sx={{
                        m: 1,
                        p: 1.5,
                        border: 'solid',
                        borderWidth: 2,
                        borderColor: 'divider',
                        maxWidth: 352,
                    }}
                >
                    <Stack direction="row" alignItems="center">
                        <CircleIcon
                            fontSize="small"                // small ≈ 16px; adjust via sx if you need exact px
                            sx={{
                                width: 11,
                                height: 11,
                                color: tooltipData.color
                            }}
                        />
                        <Box sx={{ ml: 2 }}>
                            <Typography sx={{ fontWeight: 500, mb: 1 }}>
                                <Typography
                                    component="span"
                                    sx={{ fontWeight: 700, mr: 1 }}
                                >
                                    {data.regulatedAssetTypeName}
                                </Typography>
                                {data.assetCount} assets
                            </Typography>
                            <Typography fontSize={'13px'} sx={{ fontWeight: 500, color: Grey2, textWrap: 'wrap' }}>
                                {data.description}
                            </Typography>
                        </Box>
                    </Stack>
                </Paper>
            </Popper>
        </NoSsr>
    );
}

const AssetByApplicableRegulation = () => {

    const [graphData, setGraphData] = useState([]);
    const [filters, setFilters] = useState([]);
    const [expanded, setExpanded] = useState(false);
    const [filterBy, setFilterBy] = useState(null);

    const handleChange = (_event, isExpanded) => {
        setExpanded(isExpanded);
    };

    const handleAxisDataChange = (chartAxis) => {
        setFilterBy([{
            field: 'applicableRegulations',
            operator: "is",
            value: chartAxis.axisValue
        }])
        setExpanded(true);
        setTimeout(() => {
            setFilterBy(null)
        }, 200)
    }

    useEffect(() => {
        const getData = async () => {
            const response = await fetch(Routes.RegulatedAsset.GetBarGraphRegulatedAssets(), {
                method: "POST",
                headers: {
                    Accept: "application/json",
                    "Content-Type": "application/json",
                },
            });

            const data = await response.json();
            setGraphData(data);
        }
        getData();
    }, [])

    useEffect(() => {
        const getData = async () => {
            const response = await fetch(Routes.RegulatedAsset.GetComplianceTableFilter());
            const data = await response.json();
            setFilters(data);
        }
        getData();
    }, [])

    const id = React.useId();
    const clipPathId = `${id}-clip-path`;
    return (
        <>
            {
                graphData.length > 0 && (
                    <>
                        <PageTitleWithToolTip
                            pageTitle={'Assets by Applicable Regulation'}
                            toolTipText={'View applicable federal and state regulations based on location, asset type, material type, charge capacity, and GWP. Applies to refrigeration, comfort cooling, and process cooling assets.'}
                            fontSize={'1.125rem'}
                        />
                        <Box width={"100%"} sx={{
                            backgroundColor: '#FFFFFF',
                            mb: 2,
                            borderRadius: '8px',
                            border: '1px solid #EBEDF8'
                        }}>
                            <ResponsiveChartContainer
                                height={350}
                                dataset={graphData}
                                xAxis={[{
                                    scaleType: 'band',
                                    dataKey: 'regulatedAssetTypeName',
                                    colorMap: {
                                        type: 'ordinal',
                                        colors: graphData.map(x => getChartColor(x.regulatedAssetTypeName)),
                                    },
                                }]}
                                yAxis={[{ scaleType: 'sqrt', min: 0 }]}
                                series={[{ type: 'bar', dataKey: 'assetCount' }]}
                                slotProps={{ tooltip: { trigger: 'item' } }}
                                sx={{
                                    '& .MuiBarLabel-root': { fill: '#fff', fontSize: '0.75rem' },
                                }}
                            >
                                <ChartsOnAxisClickHandler onAxisClick={(_, axisData) => handleAxisDataChange(axisData)} />
                                <BarPlot
                                    barLabel="value"
                                    barLabelSkipHeight={0}
                                    barLabelSkipWidth={0}
                                    minBarHeight={8}
                                />
                                <ChartsYAxis />
                                <ChartsXAxis />
                                <CustomItemTooltip dataSet={graphData} />
                            </ResponsiveChartContainer>
                        </Box>
                        <Accordion expanded={expanded} onChange={handleChange}>
                            <AccordionSummary
                                expandIcon={<ExpandMoreIcon />}
                                aria-controls="panel1-content"
                                id="panel1-header"
                            >
                                <Typography component="span"
                                    sx={{
                                        color: Grey1,
                                        fontFamily: "Open Sans",
                                        fontSize: '13px',
                                        fontStyle: 'normal',
                                        fontWeight: 400,
                                        lineHeight: '143%',
                                        letterSpacing: '0.17px'
                                    }}
                                >{expanded ? 'View less' : 'View more'}
                                </Typography>
                            </AccordionSummary>
                            <AccordionDetails sx={{ padding: 0 }}>
                                <DataGrid
                                    url={Routes.RegulatedAsset.GetComplianceTable()}
                                    exportButtonURL={Routes.RegulatedAsset.GetComplianceTableExport()}
                                    exportButtonAlt={"Export Applicable Reguations (.csv)"}
                                    exportFileName={"Applicable Reguations"}
                                    tableId={'asset-by-applicable-regulation-table'}
                                    rowCount="5"
                                    noResultText="Oops...It is empty here..."
                                    noResultText2="Create a new inventory or change your search criteria."
                                    filterOptions={filters}
                                    columns={columns}
                                    filterValues={filterBy}
                                />
                            </AccordionDetails>
                        </Accordion>
                    </>
                )
            }
        </>
    )
}

export default AssetByApplicableRegulation;